/**
 * Shorthand helpers for the bulk-assign drawer (Eric, 2026-10-27: '1re1' = the unit
 * with model 1RE and number 001). Pure functions, so useBulkAssign stays small.
 */

import {
  expandUnitCodeShorthand,
  normalizeUnitCode,
  parseDateRange,
  parseUnitCodes,
  scopeLinesToSites,
} from '@/lib/unitCodeParser';
import type { BulkAssignGroup, DateWindow } from './types';

/**
 * The ways one pasted token can appear in the code list: as written, padded
 * ('1RE2TCA' → '1RE002TCA'), or as unresolved shorthand ('2RE99' → '2RE099').
 */
export function writtenForms(token: string, modelCodes: string[]): string[] {
  const shorthand = parseUnitCodes(token, modelCodes).partials[0]?.token;
  return [...new Set([token, normalizeUnitCode(token), ...(shorthand ? [shorthand] : [])])];
}

/**
 * The site and dates written on the line of an ambiguous shorthand ('MED1') once
 * the user picks one of its units: the paste is re-read as if `picked` were the
 * only match, so the line's site scoping and dates apply to it.
 */
export function pickedShorthandContext({
  rawText,
  picked,
  others,
  modelCodes,
  knownCodes,
  sites,
}: {
  rawText: string;
  picked: string;
  others: string[];
  modelCodes: string[];
  knownCodes: string[];
  sites: BulkAssignGroup[];
}): { site?: string; range: DateWindow | null } {
  const codes = knownCodes.filter((c) => c === picked || !others.includes(c));
  const text = expandUnitCodeShorthand(rawText, modelCodes, codes).text;
  const site = scopeLinesToSites(text, modelCodes, sites).get(picked);
  const line = text
    .split(/\r?\n/)
    .find((l) => parseUnitCodes(l, modelCodes, { knownCodes: codes }).codes.includes(picked));
  const range = line ? parseDateRange(line, modelCodes, undefined, { knownCodes: codes }) : null;
  return { site, range };
}
